import React, { useState, useEffect } from 'react';
import moment from 'moment';
import classnames from 'classnames';
import _ from 'underscore';
import { titleize, humanize } from 'underscore.string';
import LoadingCircle from 'components/ui/LoadingCircle';
import CustomerSearch from 'components/site/customer-search';
import DatePicker from 'react-datepicker';
import { getShipments, downloadPODs } from 'utils/ShipmentWebAPIUtils';
import Pagination from 'components/v2/Pagination';
import SessionStore from 'stores/SessionStore';
import ExternalLinkIcon from 'components/v2/icons/ExternalLinkIcon';
import Badge from 'components/v2/Badge';
import { useTranslation } from 'react-i18next';
import { sluggify } from 'utils/localization/utils';

const DATE_FORMAT = 'L';
const getDateButtonClassName = (buttonType, dateSelected) => {
  return classnames({
    button: 1,
    'button-hollow-green': buttonType === dateSelected,
    'secondary-action': buttonType !== dateSelected,
  });
};

const shipmentsQueryDefaults = {
  page: 1,
  per_page: 8,
  status: ['job_closed'],
};

const ShipmentLocation = (props) => {
  const { location, type } = props;
  const { t } = useTranslation();
  if (!location || !location.length) {
    return null;
  }

  return (
    <div>
      <label className="form-label">{t(sluggify(type))}</label>
      <div>{location[0].address}</div>
    </div>
  );
};

const ShipmentLink = (props) => {
  const { t } = useTranslation();
  const { shipment } = props;
  return (
    <div className="text-sm">
      <label className="form-label">{t('SHIPMENT', { count: 1 })}</label>
      <a
        className="underline"
        href={`/shipments/${shipment._id}`}
        target="_blank"
        rel="noreferrer noopener"
      >
        <div className="flex align-middle items-center space-x-1">
          <span>{shipment.shipment_id}</span>
          <ExternalLinkIcon className="w-4 h-4" />
        </div>
      </a>
    </div>
  );
};

const ShipmentRow = (props) => {
  const { shipment } = props;
  const { t } = useTranslation();
  console.log(shipment);
  return (
    <div className="flex rounded shadow-sm p-6 space-x-2 bg-white">
      <div className="w-1/4">
        <ShipmentLink shipment={shipment} />
      </div>
      <div className="w-1/4">
        <ShipmentLocation location={shipment.origin} type="Origin" />
      </div>
      <div className="w-1/4">
        <ShipmentLocation location={shipment.destination} type="Destination" />
      </div>
      <div className="w-1/4">
        <label className="form-label">{t('STATUS')}</label>
        <Badge color="green" text={titleize(t(sluggify(shipment.status)))} />
      </div>
    </div>
  );
};

const getDateRange = (selected) => {
  if (selected === 'today') {
    return {
      deliver_after: moment().startOf('day').toISOString(),
      deliver_by: moment().endOf('day').toISOString(),
    };
  }
  if (selected === 'yesterday') {
    return {
      deliver_after: moment().subtract(1, 'day').startOf('day').toISOString(),
      deliver_by: moment().endOf('day').toISOString(),
    };
  }
  return {
    deliver_after: moment().subtract(7, 'day').startOf('day').toISOString(),
    deliver_by: moment().endOf('day').toISOString(),
  };
};

const PODDownloader = () => {
  const { t } = useTranslation();
  const [customer, setCustomer] = useState(null);
  const [dateSelected, setDateSelected] = useState('today');
  const [dateRange, setDateRange] = useState([]);
  const [startDate, endDate] = dateRange;
  const [shipments, setShipments] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [shipmentsLoaded, setShipmentsLoaded] = useState(true);
  const [user, setUser] = useState(null);
  const [downloadingFile, setDownloadingFile] = useState(false);

  const onDateChange = (date) => {
    const dates =
      date === null
        ? [moment().startOf('day'), moment().endOf('day')]
        : [moment(date).startOf('day'), moment(date).endOf('day')];

    if (date === null) {
      setDateSelected('today');
    } else {
      setDateSelected('custom');
    }

    setDateRange(dates);
  };
  const handleDateSelect = (type) => {
    setDateRange([]);
    setDateSelected(type);
  };

  useEffect(() => {
    const user = SessionStore.getUser();
    if (user._type !== 'Admin') {
      setCustomer(user);
    }
    setUser(user);
  });

  const getQuery = () => {
    let query = _.extend(shipmentsQueryDefaults, {
      [customer._type.toLowerCase()]: [customer._id],
      page,
    });

    if (startDate && endDate) {
      query = _.extend(query, {
        deliver_after: moment(startDate).startOf('day').toISOString(),
        deliver_by: moment(endDate).endOf('day').toISOString(),
      });
    } else {
      query = _.extend(query, getDateRange(dateSelected));
    }

    return query;
  };

  async function handlePODDownload() {
    setDownloadingFile(true);
    await downloadPODs({ queryOverride: getQuery() });
    setDownloadingFile(false);
  }

  useEffect(() => {
    function handleShipmentsChange(shipments) {
      setShipments(shipments);
    }

    async function queryShipments(query) {
      try {
        setShipments([]);
        setShipmentsLoaded(false);
        const { entity } = await getShipments(query);

        setShipmentsLoaded(true);
        handleShipmentsChange(entity.items);
        setPages(entity.totalPages);
        setTotal(entity.totalCount);
      } catch (e) {
        console.log('ERROR', e);
      }
    }

    if (customer && ((startDate && endDate) || dateSelected)) {
      const query = getQuery();
      queryShipments(query);
    }
  }, [page, dateSelected, customer, startDate, endDate]);

  return (
    <div className="container md:flex sm:display-block">
      <div className="md:w-1/3 space-y-6">
        <div>
          <h3>{t('POD_DOWNLOAD_AVAILABLE')}</h3>
          <span>{t('DELIVERY_PROOF_NOTE')}</span>
          <div className="my-6">
            <span>{t('SELECT_CUSTOMER_AND_DATES')}</span>
          </div>
        </div>
        {user && user._type === 'Admin' && (
          <div className="">
            <label className="form-label">{titleize(t('CUSTOMER'))}</label>
            <CustomerSearch
              clearOnSelect={false}
              onSelect={(customer, data) => setCustomer(data)}
              type="master_shippers"
              placeholder={t('SEARCH_FOR_CUSTOMERS')}
            />
          </div>
        )}
        <label className="form-label">{t('DELIVERY_DATE')}</label>
        <div className="flex space-x-4">
          <div>
            <button
              className={getDateButtonClassName('today', dateSelected)}
              onClick={() => handleDateSelect('today')}
            >
              {t('TODAY')}
            </button>
          </div>
          <div>
            <button
              className={getDateButtonClassName('yesterday', dateSelected)}
              onClick={() => handleDateSelect('yesterday')}
            >
              {t('YESTERDAY')}
            </button>
          </div>
          <div>
            <button
              className={getDateButtonClassName('this_week', dateSelected)}
              onClick={() => handleDateSelect('this_week')}
            >
              {t('LAST_SEVEN_DAYS')}
            </button>
          </div>
          <div>
            <DatePicker
              isClearable
              onChange={(date) => onDateChange(date)}
              className={getDateButtonClassName('custom', dateSelected)}
              customInput={
                <button className="pr-8" popperPlacement="bottom">
                  {t('CUSTOM')}
                </button>
              }
            />
          </div>
        </div>
        <div className="flex justify-end">
          <div>
            <button
              className="flex item-center button button-green"
              disabled={downloadingFile || shipments.length === 0}
              onClick={() => handlePODDownload()}
            >
              <i className="mr-2 mt-1 fa fa-download" />
              {t('DOWNLOAD')}
            </button>
          </div>
        </div>
        <div className="flex justify-end">
          <div>
            {downloadingFile && (
              <LoadingCircle color="white" text={t('LOADING')} />
            )}
          </div>
        </div>
      </div>
      <div className="md:w-3/4 md:pl-6 mt-4">
        {customer && startDate && endDate && (
          <div className="py-2">
            <span className="font-bold">{t('SHOWING')}:</span>
            <span>{customer.company_name}, </span>
            <span>
              {moment(startDate).format(DATE_FORMAT)} -{' '}
              {moment(endDate).format(DATE_FORMAT)}
            </span>
          </div>
        )}
        {shipments.length > 0 && (
          <div>
            <Pagination
              className=""
              perPage={shipmentsQueryDefaults.per_page}
              page={page}
              pages={pages}
              total={total}
              onPageClick={setPage}
            />
          </div>
        )}
        <div className="space-y-2 md:max-h-3/4 md:overflow-y-auto">
          {customer && shipmentsLoaded && shipments.length === 0 && (
            <span>{t('NO_POD_TRY_SEARCH')}</span>
          )}
          {!shipmentsLoaded && (
            <LoadingCircle color="green" text={t('LOADING')} />
          )}
          {shipments.map((shipment) => {
            return <ShipmentRow shipment={shipment} />;
          })}
        </div>
        {shipments.length > 0 && (
          <div>
            <Pagination
              className=""
              perPage={shipmentsQueryDefaults.per_page}
              page={page}
              pages={pages}
              total={total}
              onPageClick={setPage}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default PODDownloader;
